import React from 'react';
import { X, RefreshCw, Clock } from 'lucide-react';
import { formatErrorMessage, getErrorIcon, getErrorSeverity } from '../../utils/errorState';

const ErrorMessage = ({ error, onRetry, onDismiss, className = '' }) => {
  const formatted = formatErrorMessage(error);
  const severity = getErrorSeverity(error);
  const icon = getErrorIcon(error);

  const severityStyles = {
    critical: 'bg-red-50 border-red-200 text-red-900',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-900'
  };

  const buttonStyles = {
    critical: 'bg-red-600 hover:bg-red-700 text-white',
    error: 'bg-red-500 hover:bg-red-600 text-white',
    warning: 'bg-yellow-500 hover:bg-yellow-600 text-white'
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    return date?.toLocaleString();
  };

  return (
    <div 
      className={`border rounded-lg p-4 shadow-sm ${severityStyles?.[severity]} ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          {/* Error Icon */}
          <div className="text-2xl mt-0.5" aria-hidden="true">
            {icon}
          </div>

          {/* Error Content */}
          <div className="flex-1 min-w-0">
            {/* Error Title with Code */}
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-sm">
                {formatted?.title}
              </h3>
              {error?.code && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-black/10">
                  {error?.code}
                </span>
              )}
            </div>

            {/* Human-readable message */}
            <p className="text-sm mb-2">
              {formatted?.message}
            </p>

            {/* Technical details (collapsible) */}
            {formatted?.technical && formatted?.technical !== formatted?.message && (
              <details className="text-xs opacity-75 mb-2">
                <summary className="cursor-pointer hover:opacity-100 font-medium">
                  Technical details
                </summary>
                <p className="mt-1 font-mono bg-black/5 p-2 rounded">
                  {formatted?.technical}
                </p>
              </details>
            )}

            {/* Additional context */}
            <div className="flex flex-wrap items-center gap-3 text-xs opacity-75 mt-2">
              {/* Timestamp */}
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{formatTimestamp(error?.timestamp)}</span>
              </div>

              {/* Table */}
              {error?.table && error?.table !== 'unknown' && (
                <div className="flex items-center gap-1">
                  <span className="font-medium">Table:</span>
                  <span className="font-mono">{error?.table}</span>
                </div>
              )}

              {/* Operation */}
              {error?.operation && error?.operation !== 'unknown' && (
                <div className="flex items-center gap-1">
                  <span className="font-medium">Operation:</span>
                  <span className="font-mono">{error?.operation}</span>
                </div>
              )}

              {/* RLS Policy */}
              {error?.policyName && (
                <div className="flex items-center gap-1">
                  <span className="font-medium">Policy:</span>
                  <span className="font-mono text-xs">{error?.policyName}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 mt-3">
              {formatted?.canRetry && onRetry && (
                <button
                  onClick={() => onRetry(error?.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${buttonStyles?.[severity]}`}
                  aria-label="Retry failed operation"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retry
                </button>
              )}
              
              {onDismiss && (
                <button
                  onClick={() => onDismiss(error?.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-black/10 hover:bg-black/20 transition-colors"
                  aria-label="Dismiss error message"
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Close Button */}
        {onDismiss && (
          <button
            onClick={() => onDismiss(error?.id)}
            className="p-1 rounded hover:bg-black/10 transition-colors flex-shrink-0"
            aria-label="Close error message"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorMessage;